import React, { useEffect, useState } from 'react';
import { useNotice } from '../components/v3/shared/NoticeProvider';
import { Heading } from '../components/ui/heading';
import { fetchWithAuth } from '../utils/api';
import { FiEdit2, FiUsers, FiPlus, FiTrash } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import IconWrapper from '../components/IconWrapper';
import Modal from '../components/Modal';
import OrganizationForm from '../components/OrganizationForm';
import { Timestamp } from '../components/ui/timestamp';

// Organization interface from the API
interface Organization {
  organization_id: string;
  organization_code: string;
  name: string;
  description: string;
  created_at: string;
  updated_at: string;
}

// Form data interface for creating/updating organizations
interface OrganizationFormData {
  organization_id?: string;
  organization_code: string;
  name: string;
  description?: string;
}

const OrganizationsManagement: React.FC = () => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | undefined>(undefined);
  const navigate = useNavigate();
  const { show: showNotice } = useNotice();
  
  useEffect(() => {
    fetchOrganizations();
  }, []);
  
  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const data = await fetchWithAuth('/v1/auth/admin/organizations');
      setOrganizations(data);
      setError(null);
    } catch (err) {
      setError('Failed to load organizations. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleCreateOrganization = () => {
    setSelectedOrganization(undefined);
    setIsModalOpen(true);
  };
  
  const handleEditOrganization = (organization: Organization) => {
    setSelectedOrganization(organization);
    setIsModalOpen(true);
  };
  
  const handleSubmitOrganization = async (organizationData: OrganizationFormData) => {
    try {
      if (organizationData.organization_id) {
        // Update existing organization
        await fetchWithAuth(`/v1/auth/admin/organizations/${organizationData.organization_id}`, {
          method: 'PUT',
          body: JSON.stringify(organizationData)
        });
      } else {
        // Create new organization
        await fetchWithAuth('/v1/auth/admin/organizations', {
          method: 'POST',
          body: JSON.stringify(organizationData)
        });
      }
      
      // Close modal and refresh organizations
      setIsModalOpen(false);
      fetchOrganizations();
    } catch (err) {
      throw err;
    }
  };
  
  const handleDeleteOrganization = async (organizationId: string) => {
    // Confirm deletion
    if (!window.confirm('Are you sure you want to delete this organization? This action cannot be undone.')) {
      return;
    }
    
    try {
      await fetchWithAuth(`/v1/auth/admin/organizations/${organizationId}`, {
        method: 'DELETE'
      });
      
      // Deletion was successful if we got here (no error was thrown)
      // Remove the organization from the state without needing a server refresh
      setOrganizations(currentOrgs => 
        currentOrgs.filter(org => org.organization_id !== organizationId)
      );
      
      showNotice({ type: 'success', message: 'Organization deleted successfully', autoDismissMs: 3000 });
    } catch (err: any) {
      // Check if there are users in the organization
      if (err.message && err.message.includes('Cannot delete organization with')) {
        showNotice({ type: 'warning', message: 'This organization has users assigned. Please delete or reassign these users first.' });
      } else {
        showNotice({ type: 'error', message: 'Failed to delete organization. Please try again.' });
        console.error(err);
      }
    }
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="bg-red-50 p-4 rounded-lg text-red-800">
        <p>{error}</p>
        <button 
          onClick={fetchOrganizations}
          className="mt-2 px-4 py-2 bg-red-100 hover:bg-red-200 rounded-md text-red-800 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }
  
  return (
    <div className="bg-white shadow-sm rounded-lg p-6 max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <Heading level={1} weight="bold" className="text-secondary">Organization Management</Heading>
        <button 
          onClick={handleCreateOrganization}
          className="px-4 py-2 bg-primary text-secondary rounded-md flex items-center hover:bg-primary/90 transition-colors"
        >
          <IconWrapper icon={FiPlus} className="mr-2" />
          Add New Organization
        </button>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="text-left border-b-2 border-gray-200">
              <th className="px-4 py-3 text-sm font-semibold text-gray-600">Name</th>
              <th className="px-4 py-3 text-sm font-semibold text-gray-600">Code</th>
              <th className="px-4 py-3 text-sm font-semibold text-gray-600">Description</th>
              <th className="px-4 py-3 text-sm font-semibold text-gray-600">Created</th>
              <th className="px-4 py-3 text-sm font-semibold text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {organizations.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-4 text-center text-gray-500">
                  No organizations found
                </td>
              </tr>
            ) : (
              organizations.map(org => (
                <tr key={org.organization_id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{org.name}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{org.organization_code}</td>
                  <td className="px-4 py-3 text-gray-600">
                    {org.description ? org.description : <em className="text-gray-400">No description</em>}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    <Timestamp value={org.created_at} format="absolute" />
                  </td>
                  <td className="px-4 py-3 flex space-x-2">
                    <button 
                      className="p-1 rounded hover:bg-gray-200 text-gray-600 transition-colors"
                      onClick={() => handleEditOrganization(org)}
                      title="Edit Organization"
                    >
                      <IconWrapper icon={FiEdit2} size={16} />
                    </button>
                    <button 
                      className="p-1 rounded hover:bg-blue-100 text-blue-600 transition-colors"
                      onClick={() => navigate('/dashboard/admin/users', { state: { orgId: org.organization_id }})}
                      title="View Users"
                    >
                      <IconWrapper icon={FiUsers} size={16} />
                    </button>
                    <button 
                      className="p-1 rounded hover:bg-red-100 text-red-600 transition-colors"
                      onClick={() => handleDeleteOrganization(org.organization_id)}
                      title="Delete Organization"
                    >
                      <IconWrapper icon={FiTrash} size={16} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      {/* Organization Form Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={selectedOrganization ? 'Edit Organization' : 'Create Organization'}
      >
        <OrganizationForm
          organization={selectedOrganization}
          onSubmit={handleSubmitOrganization}
          onCancel={() => setIsModalOpen(false)}
        />
      </Modal>
    </div>
  );
};

export default OrganizationsManagement;
